import { formatPublishDate } from '@/lib/blog/dates'
import type { BlogPostMeta } from '@/lib/blog/schema'

/**
 * A vertical post card for the mood filter grid — hero image on top, then
 * title, excerpt, and date. Links to the bare `/blog/<slug>/` (the post's
 * canonical URL); a plain anchor, not the i18n `Link`, so it targets the
 * un-prefixed canonical directly instead of the `/en-us/...` form that 301s
 * back to it (same policy as `AnnouncementCard`). A post without a hero image
 * gets a plain placeholder block so rows keep their shape.
 *
 * The title is an `<h2>`: the card grid renders under the mood view's single
 * `<h1>` (see `BlogHomeContent`), so an `<h3>` would skip a heading level.
 */
export function MoodPostCard({ post }: { post: BlogPostMeta }) {
    const href = `/blog/${post.slug}/`

    return (
        <article className="flex flex-col overflow-hidden rounded-[3px] bg-card shadow-sm">
            {/* The hero-image link duplicates the title link below (same href); with
                a possibly-empty hero alt it would be an unnamed link, so it's hidden
                from assistive tech and the tab order — the title link is the one name. */}
            <a href={href} aria-hidden="true" tabIndex={-1} className="block">
                {post.heroImage ? (
                    /* Legacy hotlinked hero (interim media policy — same as post bodies);
                       plain <img>, so no next/image loader config is needed. */
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        src={post.heroImage.url}
                        alt={post.heroImage.alt}
                        width={post.heroImage.width}
                        height={post.heroImage.height}
                        className="aspect-[3/2] w-full object-cover"
                    />
                ) : (
                    <span aria-hidden="true" className="block aspect-[3/2] w-full bg-muted" />
                )}
            </a>
            <div className="flex grow flex-col gap-2 p-5">
                <h2 className="text-lg font-bold text-text-primary">
                    <a href={href} className="hover:text-text-link">
                        {post.title}
                    </a>
                </h2>
                <span aria-hidden="true" className="h-0.5 w-12 bg-primary" />
                <p className="grow text-sm leading-relaxed text-text-secondary">{post.excerpt}</p>
                <time dateTime={post.publishedAt} className="text-sm text-text-secondary">
                    {formatPublishDate(post.publishedAt)}
                </time>
            </div>
        </article>
    )
}
